import type { ChecklistItem as BoardChecklistItem } from '../../types/schema'
import type { AnchorName } from './wireGeometry'

export type DragState =
  | {
      type: 'pan'
      startClientX: number
      startClientY: number
      startScrollLeft: number
      startScrollTop: number
    }
  | {
      type: 'node'
      itemId: string
      startClientX: number
      startClientY: number
      offsetX: number
      offsetY: number
      didMove: boolean
    }
  | {
      type: 'wire'
      sourceId: string
      sourceAnchor: AnchorName
      cursor: { x: number; y: number }
      client: { x: number; y: number }
    }
  | {
      type: 'resize'
      edge: ResizeEdge
      startClientX: number
      startClientY: number
      startWidth: number
      startHeight: number
    }
  | null

export type ResizeEdge = 'top' | 'right' | 'bottom' | 'left' | 'top-left' | 'top-right' | 'bottom-right' | 'bottom-left'

export const cursorPalette = ['var(--chart-1)', 'var(--chart-2)', 'var(--chart-3)', 'var(--chart-4)', 'var(--chart-5)', 'var(--chart-6)']

export const getCursorColor = (userId: string) => {
  const hash = [...userId].reduce((sum, character) => sum + character.charCodeAt(0), 0)
  return cursorPalette[hash % cursorPalette.length]
}

export const getDistanceMeters = (
  first: { lat: number; lng: number },
  second: { lat: number; lng: number },
) => {
  const earthRadiusMeters = 6_371_000
  const firstLat = first.lat * Math.PI / 180
  const secondLat = second.lat * Math.PI / 180
  const deltaLat = (second.lat - first.lat) * Math.PI / 180
  const deltaLng = (second.lng - first.lng) * Math.PI / 180
  const haversine =
    Math.sin(deltaLat / 2) ** 2 +
    Math.cos(firstLat) * Math.cos(secondLat) * Math.sin(deltaLng / 2) ** 2

  return earthRadiusMeters * 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine))
}

export const readCurrentCoordinates = () =>
  new Promise<{ lat: number; lng: number }>((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Location verification is not available in this browser.'))
      return
    }

    navigator.geolocation.getCurrentPosition(
      (position) => resolve({
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      }),
      () => reject(new Error('Verification Rejected: location access was denied.')),
      { enableHighAccuracy: true, maximumAge: 0, timeout: 12_000 },
    )
  })

export const isTimerExpired = (node: BoardChecklistItem) => {
  if (node.timerExpiredAt) return true
  if (node.isCompleted || !node.timerStartedAt || !node.countdownHours) return false
  const startedAtMs = Date.parse(node.timerStartedAt)
  if (!Number.isFinite(startedAtMs)) return false
  return Date.now() >= startedAtMs + node.countdownHours * 60 * 60 * 1000
}
